import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { AgentDetail, AgentField, AgentUpdate } from '@schwarz/contract';
import { AGENT_INSTRUCTIONS_MAX, AGENT_NAME_MAX, AGENT_PRESET_NAME_MAX } from '@schwarz/contract';

import {
  AGENT_SAVE_CACHE_KEY,
  useGetAgentQuery,
  useUpdateAgentMutation,
} from '../../store/agents-api.js';
import { messageOf } from '../../store/error-message.js';
import { useAppDispatch } from '../../store/hooks.js';
import {
  GREETING_SAVE_CACHE_KEY,
  useGetGreetingQuery,
  useUpdateGreetingMutation,
} from '../../store/settings-api.js';
import { PlanetSpinner } from '../../ui/BrandLogo.js';
import { pushToast } from '../../ui/toast-store.js';
import { IconChevronLeft } from '../../ui/icons.js';
import { when } from '../../ui/when.js';
import { conflictOf } from './conflict.js';
import { GreetingPanel } from './GreetingPanel.js';
import styles from './AgentEditor.module.css';

/** The editable agent fields, as the form holds them. */
interface Draft {
  presetName: string;
  name: string;
  instructions: string;
}

/**
 * A draft, tagged with the agent it belongs to.
 *
 * The tag is the fix for a real bug. The draft used to be seeded by one effect
 * and cleared by another, keyed on different things; both ran on mount, the clear
 * ran second, and because a re-opened agent arrives straight from the RTK Query
 * cache the record never changed again — so the seeding effect never fired a
 * second time and the page said "Loading agent…" until a reload. One piece of
 * state carrying its own identity cannot get into that argument with itself.
 */
interface DraftState {
  agentId: string;
  values: Draft;
}

function draftOf(agent: AgentDetail): Draft {
  return {
    presetName: agent.presetName ?? '',
    name: agent.name,
    instructions: agent.instructions,
  };
}

const FIELDS = ['presetName', 'name', 'instructions'] as const;

/** "a", "a and b" — a list an operator reads rather than one a machine emits. */
function readAsList(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1] ?? ''}`;
}

/**
 * One agent's configuration (S10).
 *
 * ## What this offers, and why so little
 *
 * Preset name and instructions on both agents, plus the spoken name and the
 * greeting on the one that talks. `editableFields` arrives with the record rather
 * than being inferred here, so the form cannot offer what the API would refuse.
 *
 * Everything else the platform holds — thirty configuration keys, tools, MCP
 * servers, knowledge bases — is neither shown nor touched. A save is a full
 * overwrite on the platform's side, and the console's job is to make that safe,
 * not to put it on screen.
 *
 * ## Why one button saves two resources
 *
 * The agent lives on the Sirma platform; the greeting is ours, because the
 * platform's own first message is what greeted every contact by a demo contact's
 * surname. Two endpoints, then — but to an operator, "what the agent is called
 * and what it says" is one thing, and two save buttons on one page would be us
 * showing our seams. So this page holds both drafts, sends only the requests that
 * have something to carry, and reports on each.
 */
export function AgentEditor(): JSX.Element {
  const { agentId = '' } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { data: agent, isLoading, isError, error } = useGetAgentQuery(agentId);
  const { data: greeting } = useGetGreetingQuery();

  // The fixed keys are what let the sidebar's mark animate while these save.
  const [saveAgent, agentSaving] = useUpdateAgentMutation({ fixedCacheKey: AGENT_SAVE_CACHE_KEY });
  const [saveGreeting, greetingSaving] = useUpdateGreetingMutation({
    fixedCacheKey: GREETING_SAVE_CACHE_KEY,
  });

  const [draft, setDraft] = useState<DraftState | null>(null);
  const [greetingDraft, setGreetingDraft] = useState<string | null>(null);
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const gutterRef = useRef<HTMLDivElement>(null);

  /**
   * Seed the agent draft from the record, once per agent.
   *
   * The condition is which agent the draft belongs to, not whether the record
   * changed — a cached record does not change, and that is exactly the case that
   * used to leave this page stuck.
   */
  useEffect(() => {
    if (agent === undefined) return;

    setDraft((current) =>
      current?.agentId === agentId ? current : { agentId, values: draftOf(agent) },
    );
  }, [agent, agentId]);

  // Not keyed by agent: the greeting is one console-wide setting, shown on the
  // outbound agent's page because that is where somebody looks for it.
  useEffect(() => {
    if (greeting === undefined) return;
    setGreetingDraft((current) => current ?? greeting.template);
  }, [greeting]);

  // Never render one agent's text against another's record: the effect above runs
  // after this render, so on the first render after a switch the draft still
  // describes the agent we just left.
  const values = draft?.agentId === agentId ? draft.values : null;

  const editable = useMemo(
    () => new Set<AgentField>(agent?.editableFields ?? []),
    [agent?.editableFields],
  );

  const changed = useMemo((): AgentField[] => {
    if (agent === undefined || values === null) return [];
    const original = draftOf(agent);

    return FIELDS.filter((field) => editable.has(field) && values[field] !== original[field]);
  }, [agent, values, editable]);

  // False while the greeting is still loading, which is correct: nothing can have
  // changed against a value we have not read yet.
  const greetingChanged =
    greeting !== undefined &&
    greetingDraft !== null &&
    greetingDraft.trim() !== greeting.template.trim();

  const lineCount = useMemo(() => (values?.instructions ?? '').split('\n').length, [values]);

  const back = (): void => void navigate('/agents');

  if (isError || (!isLoading && agent === undefined)) {
    return (
      <section className={styles.view}>
        <div className={styles.head}>
          <BackButton onBack={back} />
        </div>
        <div className={styles.state}>{messageOf(error)}</div>
      </section>
    );
  }

  if (values === null || agent === undefined) {
    return (
      <section className={styles.view}>
        <div className={styles.head}>
          <BackButton onBack={back} />
        </div>
        <div className={styles.state}>
          <span className={styles.loading}>
            <PlanetSpinner maskId="ps-mark-agent" />
            Loading agent…
          </span>
        </div>
      </section>
    );
  }

  const set = (field: keyof Draft, value: string): void => {
    setDraft((current) =>
      current === null ? current : { ...current, values: { ...current.values, [field]: value } },
    );
  };

  const pending = changed.length + (greetingChanged ? 1 : 0);
  const saving = agentSaving.isLoading || greetingSaving.isLoading;

  const blankName = changed.some(
    (field) => field !== 'instructions' && values[field].trim() === '',
  );
  const blankGreeting = greetingChanged && (greetingDraft ?? '').trim() === '';

  /**
   * Send whichever requests have something to carry.
   *
   * Both are started before either is awaited, so a save that touches both costs
   * one round trip rather than two. They are then reported independently, because
   * they can fail independently — and a conflict on the agent says nothing about
   * whether the greeting should be written, so neither is held back on account of
   * the other.
   */
  const onSave = async (): Promise<void> => {
    const agentBody: AgentUpdate = {
      // Only what changed. The server treats a field equal to its current value
      // as no change anyway, but sending three fields to alter one would put the
      // other two in the audit log's blast radius for no reason.
      ...Object.fromEntries(changed.map((field) => [field, values[field]])),
      expectedSirmaUpdatedAt: agent.sirmaUpdatedAt,
    };

    const agentRequest =
      changed.length > 0 ? saveAgent({ id: agentId, body: agentBody }).unwrap() : null;

    const greetingRequest = greetingChanged
      ? saveGreeting({ template: (greetingDraft ?? '').trim() }).unwrap()
      : null;

    const saved: string[] = [];
    const failures: string[] = [];

    if (agentRequest !== null) {
      try {
        const updated = await agentRequest;
        // Re-seeded from what the server returned rather than cleared: the new
        // record carries the platform's new timestamp, which the next save needs,
        // and clearing would flash the loading state over a page the operator is
        // still looking at.
        setDraft({ agentId, values: draftOf(updated) });
        saved.push(readAsList(changed.map((field) => fieldLabels[field] ?? field)));
      } catch (failure) {
        // A conflict is kept as the operator's writing — discarding it to show
        // them somebody else's version would be the worse of the two failures.
        failures.push(conflictOf(failure) ?? messageOf(failure));
      }
    }

    if (greetingRequest !== null) {
      try {
        const updated = await greetingRequest;
        setGreetingDraft(updated.template);
        saved.push('greeting');
      } catch (failure) {
        failures.push(messageOf(failure));
      }
    }

    // Said separately, because "the greeting saved but the instructions did not"
    // is the state an operator most needs to be told plainly.
    if (saved.length > 0) {
      dispatch(pushToast('success', `Saved: ${readAsList(saved)}.`));
    }
    for (const message of failures) {
      dispatch(pushToast('error', message));
    }
  };

  return (
    <section className={styles.view}>
      <div className={styles.head}>
        <BackButton onBack={back} />

        <div className={styles.headRight}>
          {pending > 0 && (
            <span className={styles.unsaved}>
              <span className={styles.pip} aria-hidden="true" />
              {pending} unsaved change{pending === 1 ? '' : 's'}
            </span>
          )}

          <button
            type="button"
            className={styles.save}
            disabled={pending === 0 || blankName || blankGreeting || saving}
            title={
              blankName
                ? 'A name cannot be empty'
                : blankGreeting
                  ? 'The agent cannot open a call with silence'
                  : pending === 0
                    ? 'Nothing has changed'
                    : undefined
            }
            onClick={() => void onSave()}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles.panelHead}>
          <h3>Fundamentals</h3>
          <span className={styles.sub}>How this agent is identified</span>
        </div>

        <div className={styles.body}>
          <Field
            label="Agent preset name"
            hint="The display name for this agent in the dashboard and in this list."
            value={values.presetName}
            max={AGENT_PRESET_NAME_MAX}
          >
            <input
              className={styles.input}
              value={values.presetName}
              maxLength={AGENT_PRESET_NAME_MAX}
              aria-label="Agent preset name"
              onChange={(event) => set('presetName', event.target.value)}
            />
          </Field>

          {/* Only on the agent that speaks. The analyzer has a name too, and
              nobody ever hears or reads it, so there is nothing to offer. */}
          {editable.has('name') && (
            <Field
              label="Agent name"
              hint="The name the agent introduces itself with on the call. Changing it changes what contacts hear."
              value={values.name}
              max={AGENT_NAME_MAX}
            >
              <input
                className={styles.input}
                value={values.name}
                maxLength={AGENT_NAME_MAX}
                aria-label="Agent name"
                onChange={(event) => set('name', event.target.value)}
              />
            </Field>
          )}
        </div>
      </div>

      {editable.has('name') && greetingDraft !== null && (
        <GreetingPanel
          agentName={values.name}
          template={greetingDraft}
          onChange={setGreetingDraft}
          saved={greeting}
        />
      )}

      <div className={styles.panel}>
        <div className={styles.panelHead}>
          <h3>Agent instructions</h3>
          <span className={styles.sub}>
            The system prompt that defines this agent&rsquo;s behaviour
          </span>
        </div>

        <div className={styles.body}>
          <div className={styles.editor}>
            <div className={styles.gutter} ref={gutterRef} aria-hidden="true">
              {Array.from({ length: lineCount }, (_, index) => (
                <div key={index}>{index + 1}</div>
              ))}
            </div>
            <textarea
              ref={editorRef}
              className={styles.area}
              value={values.instructions}
              spellCheck={false}
              maxLength={AGENT_INSTRUCTIONS_MAX}
              aria-label="Agent instructions"
              onScroll={() => {
                if (gutterRef.current !== null && editorRef.current !== null) {
                  gutterRef.current.scrollTop = editorRef.current.scrollTop;
                }
              }}
              onChange={(event) => set('instructions', event.target.value)}
            />
          </div>

          <div className={styles.editorFoot}>
            <span>{values.instructions.length.toLocaleString()} characters</span>
            <span>{lineCount.toLocaleString()} lines</span>

            {/* Bottom right. The platform's timestamp for the agent record, which
                is the only one there is — it moves for any field, so this is
                worded as the agent changing rather than the instructions.

                `lastUpdatedBy` is an address or nothing: Sirma reports an API-key
                id for anything edited over the API, so the server resolves our
                own changes back to the person who made them and drops an id it
                cannot resolve rather than printing it. */}
            <span className={styles.byline}>
              {agent.sirmaUpdatedAt === null
                ? 'Never updated'
                : `Agent last updated${
                    agent.lastUpdatedBy === null ? '' : ` by ${agent.lastUpdatedBy}`
                  } · ${when(agent.sirmaUpdatedAt)}`}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

const fieldLabels: Record<string, string> = {
  presetName: 'preset name',
  name: 'agent name',
  instructions: 'instructions',
};

function BackButton({ onBack }: { onBack: () => void }): JSX.Element {
  return (
    <button type="button" className={styles.back} onClick={onBack}>
      <IconChevronLeft width="16" height="16" />
      Back to agents
    </button>
  );
}

interface FieldProps {
  label: string;
  hint: string;
  value: string;
  max: number;
  children: React.ReactNode;
}

function Field({ label, hint, value, max, children }: FieldProps): JSX.Element {
  return (
    <div className={styles.field}>
      <label className={styles.label}>{label}</label>
      {children}
      <div className={styles.help}>
        <span>{hint}</span>
        <span className={styles.metaRight}>
          <span className={styles.count}>
            {value.length}/{max}
          </span>
          <span className={styles.bar}>
            <span
              className={styles.barFill}
              style={{ width: `${String(Math.min(100, (value.length / max) * 100))}%` }}
            />
          </span>
        </span>
      </div>
    </div>
  );
}
